import { Card, DisplayIcon } from "@justworkshr/milo-components";
import HealthStep from "./components/HealthStep";
import { LANDING_PAGE, STEPS_DATA } from "./utils/constants";
import healthAdvocate from "assets/partners/HealthAdvocate.png";
import metLife from "assets/partners/MetLife.png";
import oneMedical from "assets/partners/OneMedical.png";
import peloton from "assets/partners/Peloton.png";
import styles from "./HealthInsuranceApplication.module.css";
import Error, { EErrorType } from "uicomponents/Error";
import { useUpdateStepMutation } from "types/generated/operations";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Alert from "uicomponents/Alert";
import {
  trackFormError,
  trackFormSubmission,
  trackPageViewUserStep,
} from "utils/analytics/trackEvent";
import { getCompanyId } from "utils/idHelpers";
import { HealthAppStepType } from "types";
import { getAppType, getCWRedirectUrl } from "utils/helper";
interface IHealthInsuranceApplicationProps {
  data: HealthAppStepType;
}

const STEP_CONTENTS_MOBILE = {
  PRE_QUALIFY: (
    <>
      <p>
        We offer a few different options for companies like yours. We’ve got
        just a few questions to make sure we get you the right info.
      </p>

      <p>
        We’ll save your progress so you can take a break if you want to switch
        to desktop, find some paperwork, or get a coffee. Sound good?
      </p>
    </>
  ),
};

const STEP_CONTENTS = {
  PRE_QUALIFY: (
    <>
      <p>
        We offer a few different options for companies like yours. We’ve got
        just a few questions to make sure we get you the right info.
      </p>
    </>
  ),
  CURRENT_INFORMATION: (
    <>
      <p>Here is what we’ll need:</p>

      <ul>
        <li>Census information for current employees and their dependents.</li>
        <li>
          Copies of your current Health Insurance Invoice, Summary of Benefits
          and Coverage, and Renewal Invoice (if applicable).
        </li>
        <li>
          Census information for any former employees & their dependents on
          COBRA (if applicable).
        </li>
      </ul>
    </>
  ),
  REVIEW_AND_SUBMIT: (
    <>
      <p>
        If you want to make any updates, you can <strong>Edit</strong> the
        information you’ve submitted.
      </p>
      <p>
        If it’s all good, then you’re ready to <strong>Submit</strong>.
      </p>
    </>
  ),
};

const STEP_ICONS = {
  PRE_QUALIFY: <DisplayIcon iconName="announcement" color="outlined" />,
  CURRENT_INFORMATION: <DisplayIcon iconName="checklist" color="outlined" />,
  REVIEW_AND_SUBMIT: <DisplayIcon iconName="document-add" color="outlined" />,
};

const HealthInsuranceApplication = (
  props: IHealthInsuranceApplicationProps
) => {
  const { data } = props;

  const appType = getAppType();
  const isHealthInsuranceUpsellApp = appType === "HEALTH_INSURANCE_UPSELL";

  const [updateStep] = useUpdateStepMutation();
  const navigate = useNavigate();

  const [isError, setIsError] = useState(false);

  const applicationStep = data?.applications?.[0]?.step || 0;
  const isNotEligibleForCoverage =
    data?.healthInsuranceInformation?.isCoverageEligible === false;
  const companyId = getCompanyId();

  const preQualStarted = applicationStep > 1 && applicationStep < 5;
  const currentInformationStarted = applicationStep > 5 && applicationStep < 8;

  useEffect(() => {
    if (applicationStep === 9) {
      const isHealthInsuranceUpsellAppLocalScope =
        getAppType() === "HEALTH_INSURANCE_UPSELL";
      const redirectPath = STEPS_DATA.REVIEW_AND_SUBMIT.redirectPath;
      const routeUrl = isHealthInsuranceUpsellAppLocalScope
        ? redirectPath + "-mdv"
        : redirectPath;
      navigate(`/${routeUrl}/${companyId}`);
      return;
    }
    window.scrollTo(0, 0);
    trackPageViewUserStep(0, "landing page");
  }, [navigate, applicationStep, companyId, isHealthInsuranceUpsellApp]);

  if (!data?.applications?.[0]) {
    return <Error />;
  }

  if (isNotEligibleForCoverage) {
    return <Error type={EErrorType.NotEligibleForCoverage} />;
  }

  const updateStepAndHandleRedirect = async (redirectPath: string) => {
    if (!data?.applications?.[0]) return;
    const isHealthInsuranceUpsellAppLocalScope =
      getAppType() === "HEALTH_INSURANCE_UPSELL";
    const routeUrl = isHealthInsuranceUpsellAppLocalScope
      ? redirectPath + "-mdv"
      : redirectPath;
    try {
      if ([0, 4, 8].includes(applicationStep)) {
        await updateStep({
          variables: {
            uuid: data?.applications?.[0].uuid,
            step: applicationStep + 1,
          },
        });

        if (applicationStep === 8) {
          trackFormSubmission(applicationStep, `${appType} submitted`);
          navigate(`/${routeUrl}/${companyId}`);
        } else {
          navigate(`/${routeUrl}/${companyId}/${applicationStep + 1}`);
        }
        return;
      }
      navigate(`/${routeUrl}/${companyId}/${applicationStep}`);
    } catch (error: any) {
      trackFormError(
        error,
        `error updating ${appType} form step- ${error?.message}`
      );
      setIsError(true);
      return;
    }
  };

  const handleEditClick = (redirectPath: string, stepNumber: number) => {
    const isHealthInsuranceUpsellAppLocalScope =
      getAppType() === "HEALTH_INSURANCE_UPSELL";
    const routeUrl = isHealthInsuranceUpsellAppLocalScope
      ? redirectPath + "-mdv"
      : redirectPath;
    navigate(`/${routeUrl}/${companyId}/${stepNumber}?isEdit=true`);
  };

  return (
    <div className={styles.landingPageContainer}>
      <div className={styles.anchorDiv}>
        <a
          className={styles.backToDashboardLink}
          href={
            isHealthInsuranceUpsellApp
              ? getCWRedirectUrl("/benefits/offer")
              : getCWRedirectUrl("/onboarding_checklist")
          }
        >
          {isHealthInsuranceUpsellApp
            ? LANDING_PAGE.backToBenefits
            : LANDING_PAGE.backToDashboard}
        </a>
      </div>
      <div className={styles.header}>
        <h1>{LANDING_PAGE.pageTitle}</h1>
      </div>

      <section className={styles.landingPageContent}>
        <div className={styles.steps}>
          <HealthStep
            title={STEPS_DATA.PRE_QUALIFY.stepTitle}
            status={STEPS_DATA.PRE_QUALIFY.getStatus(applicationStep)}
            iconBackgroundColor={STEPS_DATA.PRE_QUALIFY.iconBackground}
            updateStepAndHandleRedirect={() =>
              updateStepAndHandleRedirect(STEPS_DATA.PRE_QUALIFY.redirectPath)
            }
            icon={STEP_ICONS.PRE_QUALIFY}
            description={STEP_CONTENTS.PRE_QUALIFY}
            mobileDescription={STEP_CONTENTS_MOBILE.PRE_QUALIFY}
            handleEditClick={() =>
              handleEditClick(STEPS_DATA.PRE_QUALIFY.redirectPath, 1)
            }
            buttonText={preQualStarted ? "Continue" : ""}
          />

          <div className={styles.divider}></div>

          <HealthStep
            title={STEPS_DATA.CURRENT_INFORMATION.stepTitle}
            status={STEPS_DATA.CURRENT_INFORMATION.getStatus(applicationStep)}
            updateStepAndHandleRedirect={() =>
              updateStepAndHandleRedirect(
                STEPS_DATA.CURRENT_INFORMATION.redirectPath
              )
            }
            iconBackgroundColor={STEPS_DATA.CURRENT_INFORMATION.iconBackground}
            icon={STEP_ICONS.CURRENT_INFORMATION}
            description={STEP_CONTENTS.CURRENT_INFORMATION}
            handleEditClick={() =>
              handleEditClick(STEPS_DATA.CURRENT_INFORMATION.redirectPath, 5)
            }
            buttonText={currentInformationStarted ? "Continue" : ""}
          />

          <div className={styles.divider}></div>

          <HealthStep
            title={STEPS_DATA.REVIEW_AND_SUBMIT.stepTitle}
            status={STEPS_DATA.REVIEW_AND_SUBMIT.getStatus(applicationStep)}
            updateStepAndHandleRedirect={() =>
              updateStepAndHandleRedirect(
                STEPS_DATA.REVIEW_AND_SUBMIT.redirectPath
              )
            }
            buttonText={STEPS_DATA.REVIEW_AND_SUBMIT.buttonText}
            iconBackgroundColor={STEPS_DATA.REVIEW_AND_SUBMIT.iconBackground}
            icon={STEP_ICONS.REVIEW_AND_SUBMIT}
            description={STEP_CONTENTS.REVIEW_AND_SUBMIT}
            handleEditClick={() =>
              handleEditClick(STEPS_DATA.REVIEW_AND_SUBMIT.redirectPath, 8)
            }
          />
        </div>

        <div className={styles.information}>
          <Card className={styles.infoBox} title="Benefits your team will love">
            <div className={styles.partners}>
              <img src={metLife} alt="met life logo" />
              <img src={oneMedical} alt="one medical logo" />
              <img src={peloton} alt="peloton logo" />
              <img src={healthAdvocate} alt="health advocate logo" />
            </div>
          </Card>

          <Card
            className={styles.infoBox}
            title="Why do we need this information"
          >
            <div>{LANDING_PAGE.informationBox}</div>
          </Card>
        </div>
      </section>
      {isError && <Alert />}
    </div>
  );
};
export default HealthInsuranceApplication;
